import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormLabel, InputLabel, makeStyles, MenuItem, Select, Switch, Tooltip } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { WordsPerMinute } from '../sermonAnalyzer';
import { useBuilder } from '../../useBuilder';
import { useEditor } from '../../editor/useEditor';
import { BuilderPreferences } from '../../../../entities/BuilderPreferences';

const useStyles = makeStyles((theme) => ({
    mobileSpacing: {
        [theme.breakpoints.down("xs")]: {
            marginTop: "2em !important",
        },
    },
}));

interface Props {
    openSettings: boolean;
    currentWordsPerMinute: number;
    closeSettingsDialog: () => void;
    setWordsPerMinute: (value:number) => void;
}

export function AnalyzerSettingsDialog(props:Props) {
    const builder = useBuilder();
    const editor = useEditor();
    const classes = useStyles();
    
    const {openSettings, currentWordsPerMinute, closeSettingsDialog, setWordsPerMinute } = props;
    const {hideConsecutiveSentences, hideFleschReadingEase, hideParagraphLength, hidePassiveVoice, 
        hideScriptureSaturation, hideSentenceLength,hideSubheadingDistribution, hideTransitionalWords} = builder.preferences;

    const [ consecutiveSentences, setConsecutiveSentences ] = useState<boolean>(hideConsecutiveSentences)
    const [ fleschReadingEase, setFleschReadingEase ] = useState<boolean>(hideFleschReadingEase)
    const [ paragraphLength, setParagraphLength ] = useState<boolean>(hideParagraphLength)
    const [ passiveVoice, setPassiveVoice ] = useState<boolean>(hidePassiveVoice)
    const [ scriptureSaturation, setScriptureSaturation ] = useState<boolean>(hideScriptureSaturation)
    const [ sentenceLength, setSentenceLength ] = useState<boolean>(hideSentenceLength)
    const [ subheadingDistribution, setSubheadingDistribution ] = useState<boolean>(hideSubheadingDistribution)
    const [ transitionalWords, setTransitionalWords ] = useState<boolean>(hideTransitionalWords)

    const toggleConsecutiveSentences = () => setConsecutiveSentences(!consecutiveSentences);
    const toggleFleschReadingEase = () => setFleschReadingEase(!fleschReadingEase);
    const toggleParagraphLength = () => setParagraphLength(!paragraphLength);
    const togglePassiveVoice = () => setPassiveVoice(!passiveVoice);
    const toggleScriptureSaturation= () => setScriptureSaturation(!scriptureSaturation);
    const toggleSentenceLength = () => setSentenceLength(!sentenceLength);
    const toggleSubheadingDistribution = () => setSubheadingDistribution(!subheadingDistribution);
    const toggleTransitionalWords = () => setTransitionalWords(!transitionalWords);

    const onSaveSettings = () => {
        let updatedPreferences:BuilderPreferences = { ...builder.preferences };
        updatedPreferences.hideConsecutiveSentences = consecutiveSentences;
        updatedPreferences.hideFleschReadingEase = fleschReadingEase;
        updatedPreferences.hideParagraphLength = paragraphLength;
        updatedPreferences.hidePassiveVoice = passiveVoice;
        updatedPreferences.hideScriptureSaturation = scriptureSaturation;
        updatedPreferences.hideSentenceLength = sentenceLength;
        updatedPreferences.hideSubheadingDistribution = subheadingDistribution;
        updatedPreferences.hideTransitionalWords = transitionalWords;
        builder.savePreferences(updatedPreferences);
        editor.updateAnalyzerResults();
        closeSettingsDialog();
    }

    return (
        <Dialog
        open={openSettings}
        onClose={closeSettingsDialog}>
        <DialogTitle>Sermon Analyzer Settings</DialogTitle>
        <DialogContent>
            <FormControl fullWidth margin="normal">
                <Tooltip title="Select Preaching Speed" placement="top">
                    <InputLabel htmlFor="WordsPerMinute" style={{ fontSize: 21 }}>
                        Preaching Speed <InfoIcon style={{ fontSize: 17 }} />
                    </InputLabel>
				</Tooltip>
                <Select 
                    inputProps={{
                        name: "WordsPerMinute",
                        id: "WordsPerMinute",
                    }}
                	className={classes.mobileSpacing}
                    value={currentWordsPerMinute}
                    onChange={(e:React.ChangeEvent<{value: unknown}>) => setWordsPerMinute(e.target.value as number)}
                    >
                    {
                        Object.entries(WordsPerMinute).map( ([name, value]) => <MenuItem key={value} value={value}>{name}</MenuItem> )
                    }
                </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
                <Tooltip title="Toggle Text Improvements to Display" placement="top">
                    <FormLabel htmlFor="Improvements" style={{ fontSize: 21, transform: "translate(0, 1.5px) scale(0.75)", transformOrigin: 'top left' }}>
                        Text Improvements: <InfoIcon style={{ fontSize: 17 }} />
                    </FormLabel>
                </Tooltip>
                <FormGroup id="Improvements" className={classes.mobileSpacing} row>
                    <FormGroup>
                    <FormControlLabel 
                    className={classes.mobileSpacing}
                    control={ <Switch color="primary" onChange={toggleConsecutiveSentences} checked={!consecutiveSentences}/> }
                    label="Consecutive Sentences"
                    />
                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={toggleFleschReadingEase} checked={!fleschReadingEase} />}
                        label="Flesch Reading Ease"
                        />
                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={toggleParagraphLength} checked={!paragraphLength} />}
                        label="Paragraph Length"
                        />
                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={togglePassiveVoice} checked={!passiveVoice} />}
                        label="Passive Voice"
                        />
                    </FormGroup>
                    <FormGroup>

                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={toggleScriptureSaturation} checked={!scriptureSaturation} />}
                        label="Scripture Saturation"
                        />
                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={toggleSentenceLength} checked={!sentenceLength} />}
                        label="Sentence Length"
                        />
                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={toggleSubheadingDistribution} checked={!subheadingDistribution} />}
                        label="Subheading Distribution"
                        />
                    <FormControlLabel
                        className={classes.mobileSpacing} 
                        control={<Switch color="primary" onChange={toggleTransitionalWords} checked={!transitionalWords} />}
                        label="Transitional Words"
                        />
                        </FormGroup>
                </FormGroup>
            </FormControl>

            <DialogActions>
                <Button onClick={closeSettingsDialog} color="primary">
                    Cancel
                </Button>
                <Button onClick={onSaveSettings} color="primary">
                    Apply Settings
                </Button>
            </DialogActions>
        </DialogContent>
    </Dialog>
    )
}